import { Location } from '@angular/common';
import { inject, InjectionToken } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { WINDOW } from '@campus/browser';
import {
  AuthService,
  ContextInterface,
  EduContent,
  EduContentService,
  EduFile,
  EduFileInterface,
  Link,
  Permissions,
  ScormCmiMode,
  UserQueries,
} from '@campus/dal';
import { ExerciseService, NormalExerciseConfigurationInterface } from '@campus/exercise';
import { select, Store } from '@ngrx/store';
import { Observable, of, take } from 'rxjs';
import { PermissionService } from '../../../auth';
import { OPEN_STATIC_CONTENT_SERVICE_TOKEN, OpenStaticContentOptions } from '../../content';
import {
  ContentOpenerMethodsType,
  ContentOpenerNewInterface,
  OpenContentOptions,
} from './content-open-actions.service.interface';
/**
 * Interface for providing additional context and side effects when opening content.
 * Implementations can enrich the context with domain-specific data and execute
 * business logic side effects like tracking, analytics, or state management.
 */
export interface ContentOpenerContextProviderInterface {
  /**
   * Provides additional context data for content opening operations.
   * @param eduContent - The educational content being opened
   * @param context - Merged context (preset and runtime) that can be used to derive additional data
   * @returns Observable of context data to be merged with default context
   */
  getContext$?(eduContent: EduContent, context: ContextInterface): Observable<ContextInterface>;
  /**
   * Executes side effects after content opening operations.
   * Used for tracking, analytics, state updates, or other business logic.
   * @param action - The type of content opening action performed
   * @param eduContent - The educational content that was opened
   * @param options - The options used for opening the content
   */
  executeSideEffects?(action: ContentOpenerMethodsType, eduContent: EduContent, context: ContextInterface): void;
}

/**
 * Injection token for the optional context provider service.
 * Allows different modules to provide their own context enhancement logic.
 */
export const CONTENT_OPENER_CONTEXT_PROVIDER = new InjectionToken<ContentOpenerContextProviderInterface>(
  'CONTENT_OPENER_CONTEXT_PROVIDER'
);
/**
 * Default implementation of ContentOpenerInterface that provides fallback behavior
 * for opening various types of educational content.
 *
 * The service uses a context provider pattern to allow other modules to:
 * - Inject additional context data (like unlocked practice IDs)
 * - Execute side effects (like tracking or state management)
 *
 */
export class DefaultContentOpenerService implements ContentOpenerNewInterface {
  private router = inject(Router);
  private location = inject(Location);
  private window = inject(WINDOW);
  private contextProvider = inject(CONTENT_OPENER_CONTEXT_PROVIDER, { optional: true });
  private authService = inject(AuthService);
  private exerciseService = inject(ExerciseService);
  private openStaticContentService = inject(OPEN_STATIC_CONTENT_SERVICE_TOKEN);
  private eduContentService = inject(EduContentService);
  private store = inject(Store);
  private permissionService = inject(PermissionService);
  private canPrintRichText = toSignal(
    this.permissionService.hasPermission$([Permissions.feature.CAN_PRINT_RICH_TEXT]),
    { initialValue: false }
  );

  public openEduContentAsExercise(
    eduContent: EduContent,
    options: OpenContentOptions,
    context: ContextInterface
  ): void {
    const playerOptions = {
      withFeedback: options?.withFeedback === undefined ? true : options.withFeedback, // when not set, default to true
      isRepeatable: options?.isRepeatable,
    };
    this.withMergedContext(eduContent, context, (mergedContext) => {
      const config: NormalExerciseConfigurationInterface = {
        mode: ScormCmiMode.CMI_MODE_NORMAL,
        userId: this.authService.userId,
        eduContent,
        context: mergedContext,
        playerOptions,
      };
      this.exerciseService.loadExercise(config);
      this.contextProvider?.executeSideEffects?.('openEduContentAsExercise', eduContent, mergedContext);
    });
  }

  public openEduContentForReview(
    eduContent: EduContent,
    options: OpenContentOptions,
    context: ContextInterface = {}
  ): void {
    const forResult = options?.forResult;
    if (!forResult) return;
    const isStudent = forResult.personId === this.authService.userId;
    if (!isStudent) return this.exerciseService.reviewExerciseAsTeacher(eduContent, forResult);

    this.store
      .select(UserQueries.getCurrentUser)
      .pipe(take(1))
      .subscribe((person) => {
        this.withMergedContext(eduContent, context, (mergedContext) => {
          this.exerciseService.reviewExerciseAsStudent(eduContent, { ...forResult, person }, mergedContext);
        });
      });
  }

  public openEduContentAsStream(eduContent: EduContent, options: OpenContentOptions, context: ContextInterface): void {
    this.openStaticContent('openEduContentAsStream', eduContent, { stream: true }, context);
  }

  public openEduContentAsDownload(
    eduContent: EduContent,
    options: OpenContentOptions,
    context: ContextInterface
  ): void {
    this.openStaticContent('openEduContentAsDownload', eduContent);
  }

  public openEduContentAsLink(eduContent: EduContent, options: OpenContentOptions, context: ContextInterface): void {
    this.openStaticContent('openEduContentAsLink', eduContent);
  }

  public openBoeke(eduContent: EduContent, options: OpenContentOptions, context: ContextInterface): void {
    this.openStaticContent('openBoeke', eduContent);
  }

  public openEduContentAsHtml(eduContent: EduContent, options: OpenContentOptions, context: ContextInterface): void {
    this.store.pipe(select(UserQueries.getCurrentUser), take(1)).subscribe((user) => {
      const isTeacher = (user?.roles || []).some((role) => role.name === 'teacher');
      const canPrint = eduContent.isRichText && isTeacher && this.canPrintRichText();
      this.openStaticContent('openEduContentAsHtml', eduContent, {
        showPreview: true,
        disableMathjax: options?.disableMathjax,
        canPrint,
      });
    });
  }

  public openPaperExercise(eduContent: EduContent, options: OpenContentOptions, context: ContextInterface): void {
    this.withMergedContext(eduContent, context, (mergedContext) => {
      this.eduContentService.printEduContent({
        eduContentId: eduContent.id,
        ufpId: mergedContext?.unlockedFreePracticeId,
        taskId: mergedContext?.taskId,
      });
    });
  }

  public openPaperExerciseAsSolution(
    eduContent: EduContent,
    options: OpenContentOptions,
    context: ContextInterface
  ): void {
    this.withMergedContext(eduContent, context, (mergedContext) => {
      this.eduContentService.printEduContent({
        eduContentId: eduContent.id,
        ufpId: mergedContext?.unlockedFreePracticeId,
        taskId: mergedContext?.taskId,
        isSolution: true,
      });
    });
  }

  public openInstructions(eduContent: EduContent, options: OpenContentOptions, context: ContextInterface): void {
    const { artifactId } = eduContent.publishedEduContentMetadata;

    const path = this.router.createUrlTree(['/content', 'instructions', artifactId]).toString();
    const fullPath = this.location.prepareExternalUrl(path);

    // no need to add the domain since we're already on the same domain
    this.window.open(fullPath, '_blank');
  }

  public openEduFileAsDownload(eduContent: EduContent, options: OpenContentOptions, context: ContextInterface): void {
    const eduFile = eduContent.publishedEduContentMetadata.eduFiles.find(
      (file: EduFileInterface) => file.id === options?.eduFileId
    );
    this.openStaticContentService.open(new EduFile(eduFile));
  }

  public openLink(eduContent: EduContent, options: OpenContentOptions, context: ContextInterface): void {
    const link = eduContent.publishedEduContentMetadata.links.find((l: Link) => l.id === options?.linkId);
    this.openStaticContentService.open(new Link(link));

    // this.window.open(link.url, '_blank');
  }

  /**
   * Private helper method for opening static content with various settings.
   * Merges context data, opens content via static service, and tracks the opening
   * by dispatching a result action to the store.
   *
   * @param eduContent - The educational content to open
   * @param settings - Display and behavior settings for the content
   * @param options - Opening options including context data
   */
  private openStaticContent(
    action: ContentOpenerMethodsType,
    eduContent: EduContent,
    settings: OpenStaticContentOptions = {},
    context: ContextInterface = {}
  ): void {
    this.withMergedContext(eduContent, context, (mergedContext) => {
      this.openStaticContentService.open(eduContent, {
         ...settings ,
        context: mergedContext,
      });

      this.contextProvider.executeSideEffects?.(action, eduContent, mergedContext);
    });
  }

  /**
   * Merges context from multiple sources and executes a callback with the final context.
   *
   * Context is built by:
   * 1. Extracting basic context from options (bookId, tocId)
   * 2. Requesting additional context from the optional context provider
   * 3. Merging both contexts (provider context takes precedence)
   * 4. Executing the callback with the merged context
   *
   * @param eduContent - The educational content being opened
   * @param options - Opening options containing basic context
   * @param cb - Callback to execute with the merged context
   */
  private withMergedContext(
    eduContent: EduContent,
    context: ContextInterface,
    cb: (mergedContext: ContextInterface) => void
  ): void {
    const contextFromProvider$ = this.contextProvider?.getContext$
      ? this.contextProvider.getContext$(eduContent, context)
      : of({});
    contextFromProvider$.pipe(take(1)).subscribe((contextFromProvider) =>
      cb({
        ...context,
        ...contextFromProvider,
      })
    );
  }
}
